import { t } from "@lingui/macro";
import { useState } from "react";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import type { promoCodeAnalytics_booking$key } from "./__generated__/promoCodeAnalytics_booking.graphql";
import { AppliedPromoCodeState } from "./components/AppliedPromoCodeState/AppliedPromoCodeState";
import { CollapsedPromoCodeState } from "./components/CollapsedPromoCodeState/CollapsedPromoCodeState";
import { ExpandedPromoCodeState } from "./components/ExpandedPromoCodeState/ExpandedPromoCodeState";
import { useApplyPromoCodeToBooking } from "./hooks/useApplyPromoCodeToBooking";
import {
  getAppliedPromoCodeAnalyticsData,
  getPromoCodeAnalyticsContext,
} from "./promoCodeAnalytics";

type Props = {
  bookingId: string;
  bookingAnalyticsRef: promoCodeAnalytics_booking$key | null;
  appliedCode?: string | null;
  calculationType?: string | null;
  discountAmount?: number | null;
  discountType?: string | null;
  isEditable: boolean;
};

export const BookingPromoCode = ({
  appliedCode,
  bookingId,
  bookingAnalyticsRef,
  calculationType,
  discountAmount,
  discountType,
  isEditable,
}: Props) => {
  const [applyPromoCodeToBooking, isApplyingPromoCode] =
    useApplyPromoCodeToBooking();
  const [isEntryOpen, setIsEntryOpen] = useState(false);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const trackEvent = useTrackEvent();
  const analyticsContext = getPromoCodeAnalyticsContext(bookingAnalyticsRef);
  const appliedPromoCodeAnalytics =
    getAppliedPromoCodeAnalyticsData(bookingAnalyticsRef);

  const handleApply = () => {
    const normalizedCode = code.trim();

    if (!normalizedCode) {
      const message = t`Enter a promo code.`;
      setErrorMessage(message);
      trackEvent("PromoCodeNotAccepted", {
        ...analyticsContext,
        promoCodeEntered: normalizedCode,
        errorGiven: message,
      });
      return;
    }

    setErrorMessage(null);
    applyPromoCodeToBooking(
      { bookingId, promoCode: normalizedCode },
      (analyticsData) => {
        if (analyticsData) {
          trackEvent("PromoCodeApplied", analyticsData);
        }
        setCode("");
        setIsEntryOpen(false);
      },
      (message) => {
        setErrorMessage(message);
        trackEvent("PromoCodeNotAccepted", {
          ...analyticsContext,
          promoCodeEntered: normalizedCode,
          errorGiven: message,
        });
      }
    );
  };

  const handleRemove = () => {
    setErrorMessage(null);
    applyPromoCodeToBooking(
      { bookingId, promoCode: null },
      () => {
        if (appliedPromoCodeAnalytics) {
          trackEvent("PromoCodeRemoved", {
            department: appliedPromoCodeAnalytics.department,
            facility: appliedPromoCodeAnalytics.facility,
            promoCode: appliedPromoCodeAnalytics.promoCode,
            discountType: appliedPromoCodeAnalytics.discountType,
            discountValue: appliedPromoCodeAnalytics.discountValue,
          });
        }
        setCode("");
        setIsEntryOpen(false);
      },
      setErrorMessage
    );
  };

  const handleCancel = () => {
    setCode("");
    setErrorMessage(null);
    setIsEntryOpen(false);
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setErrorMessage(null);
  };

  if (appliedCode) {
    return (
      <AppliedPromoCodeState
        appliedCode={appliedCode}
        calculationType={calculationType}
        discountAmount={discountAmount}
        discountType={discountType}
        errorMessage={errorMessage}
        isApplyingPromoCode={isApplyingPromoCode}
        isEditable={isEditable}
        onRemove={handleRemove}
      />
    );
  }

  if (!isEditable) {
    return null;
  }

  if (isEntryOpen) {
    return (
      <ExpandedPromoCodeState
        code={code}
        errorMessage={errorMessage}
        isApplyingPromoCode={isApplyingPromoCode}
        onApply={handleApply}
        onCancel={handleCancel}
        onCodeChange={handleCodeChange}
      />
    );
  }

  return (
    <CollapsedPromoCodeState
      onOpen={() => {
        trackEvent("PromoCodeEntryOpened", analyticsContext);
        setIsEntryOpen(true);
      }}
    />
  );
};
